import type { ButtonProps } from './Button';
import type { ButtonAppearance, ButtonAppearanceProps, ButtonSize } from 'components/Button/types';

/**
 * Maps the legacy MUI `Button` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at 244 call sites; retiring the flag means deleting it and letting call sites
 * speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS components cannot express this button" and sends the caller back
 * to MUI. That is a deliberate design: a silent best-effort mapping would ship wrong-looking or
 * behaviourally different buttons, and there is no way to notice at review time. Every `null` below
 * is a design question that has to be answered before that call site can be refreshed.
 */
type DsProps = ButtonAppearanceProps & { size: ButtonSize; loading?: boolean };

/**
 * `muted` is excluded from the value type, not just absent from the table: the appearance looked up
 * here is spread into a `{ appearance, fill }` alongside `default` or `outline`, and `muted` draws
 * no `default`, so a table that could yield it would not type-check against `ButtonAppearanceProps`.
 *
 * `Exclude` rather than naming the three appearances the table holds, which would read tighter but
 * say less: the rule the spread below needs is "an appearance that draws a solid fill", and a list of
 * today's rows states the contents instead of the rule. So the value type is deliberately WIDER than
 * the data — it admits `success`, which no MUI colour maps to — and it follows the appearance axis if
 * another cell ever loses its `default`. Inferring it from the object with `as const satisfies` would
 * be exact instead, but then `info` could not index the table at all and the lookup below would stop
 * compiling.
 *
 * `info` is intentionally absent for a different reason: DS 2.0 has no informational appearance, and
 * there are zero `color="info"` call sites, so guessing at a neutral would invent a mapping nothing
 * needs.
 */
const APPEARANCE: Partial<Record<NonNullable<ButtonProps['color']>, Exclude<ButtonAppearance, 'muted'>>> = {
  primary: 'primary',
  secondary: 'secondary',
  error: 'destructive',
};

/**
 * Mapped by HEIGHT, not by name.
 *
 * MUI buttons are 24 / 32 / 40 (`MuiButton.ts` sizeSmall/Medium/Large); DS buttons are 32 / 36 / 40
 * / 44. Matching the names instead — small->small, medium->regular, large->large — inflated every
 * button it touched by 4-12px, so turning the flag on silently grew buttons and reflowed whatever
 * sat beside them. Only exact height matches are mapped:
 *
 *   medium (32) -> xs      (32)
 *   large  (40) -> regular (40)
 *
 * `small` (24) has no DS counterpart — the scale stops at 32 — so it stays on MUI rather than
 * growing 8px. That is the same rule `Buttons/IconButton/toDsIconProps` applies.
 */
const SIZE: Partial<Record<NonNullable<ButtonProps['size']>, ButtonSize>> = {
  medium: 'xs',
  large: 'regular',
};

/**
 * Cases the DS components cannot express, all of which stay on MUI:
 *
 *   - `loadingIndicator` — a caller-supplied MUI spinner. The DS button draws its own, so honouring
 *     a custom one would mean rendering an Emotion `CircularProgress` inside a Tailwind button.
 *   - `sx` — an Emotion prop with no effect on a Tailwind component. Accepting it would silently
 *     drop the styles rather than fail, so ~40 call sites would quietly lose their spacing.
 *   - `variant="light" | "text-no-padding" | "grayed"` — theme-local variants
 *     (`common/theme/components/MuiButton.ts`) with no DS counterpart. `text-no-padding` in
 *     particular is a zero-height inline text button, which the recipe's fixed heights rule out.
 *   - `color="info"` — see above.
 *   - a disabled link — the platform has no such thing, and `ButtonLink` accordingly has no
 *     `disabled` prop. Rare enough to leave on MUI rather than design around.
 */
export const toDsProps = ({
  color = 'primary',
  disabled,
  externalHref,
  href,
  isLoading,
  loadingIndicator,
  size = 'medium',
  sx,
  variant = 'contained',
}: ButtonProps): DsProps | null => {
  if (sx || loadingIndicator) {
    return null;
  }

  // A link cannot be disabled or busy: `ButtonLink` has no such state, and the platform has no
  // inert anchor.
  if ((href || externalHref) && (disabled || isLoading)) {
    return null;
  }

  const dsSize = SIZE[size];

  if (!dsSize) {
    return null;
  }

  const base = { size: dsSize, loading: isLoading };

  // Early returns rather than one built object: `ButtonAppearanceProps` is a discriminated union, so
  // a flat `{ appearance, fill }` assembled from variables satisfies neither member.
  if (variant === 'text') {
    // `ghost` is muted-only in the Figma matrix, so `variant="text"` implies the muted appearance.
    return { appearance: 'muted', fill: 'ghost', ...base };
  }

  /**
   * The one theme-local variant with a real DS counterpart: `outlined-grey` is a neutral outline
   * button (white, grey[300] border, grey[700] label), which is what `muted / outline` draws.
   *
   * `color` is ignored on purpose. The MUI variant hardcodes its own colours and overrides whatever
   * `color` was passed (`MuiButton.ts:144-164`), so honouring `color` here would make the DS render
   * differ from the MUI one it replaces — `Dialog.tsx` passes `color="secondary"` that today has no
   * effect whatsoever.
   */
  if (variant === 'outlined-grey') {
    return { appearance: 'muted', fill: 'outline', ...base };
  }

  if (variant !== 'contained' && variant !== 'outlined') {
    return null;
  }

  const appearance = APPEARANCE[color];

  return appearance ? { appearance, fill: variant === 'outlined' ? 'outline' : 'default', ...base } : null;
};
